<template>
  <header id="header">
    <div class="header-grid">
      <div class="header-title-row">
        <h1 class="title is-3 has-text-centered">{{ appDisplayName }}</h1>
        <a href="#" class="light_circle" @click="is_popup_open = true" title="Help">?</a>
      </div>
      <div id="header_image">
        <a href="https://timeuse.ipums.org">
          <img src="https://assets.ipums.org/_img/logo-Time-Use.png" alt="IPUMS Time Use"/>
        </a>
      </div>
      <div id="topics_legend">
        <div class="filters_container">
          <div class="filters-row">
            <dropdown title="Show ATUS Counts">
              <div class="dropdown-item">
                <fieldset>
                  <legend><b>Years</b></legend>
                  <ul class="checkbox-list">
                    <li class="parent">
                      <input type="checkbox" id="years_select_all" :checked="years_all_selected()"
                             @change="toggle_years_select_all">
                      <label for="years_select_all"><b>Show All Counts</b></label>
                    </li>
                    <li v-for="year in Object.keys(atus_samples).reverse()">
                      <input type="checkbox" :id="'sample_atus_' + year" :checked="atus_samples[year]['picked']"
                             @change="toggle_year($event, atus_samples[year])">
                      <label :for="'sample_atus_' + year" class="year-row">
                        <span class="year-text">{{ atus_samples[year].name }}</span>
                        <span class="year-modules">
                          <span v-for="module_name in sorted_atus_modules_for_year(year)"
                                :key="module_name"
                                :class="['tag', 'is-rounded', 'year-module-pill', atus_module_name_to_pill_class[module_name]]">
                            {{ atus_module_name_to_abbrev[module_name] }}
                          </span>
                        </span>
                      </label>
                    </li>
                  </ul>
                </fieldset>
                <fieldset>
                  <legend><b>Modules</b></legend>
                  <div class="module-legend-grid">
                    <span v-for="(name, index) in sorted_atus_module_names" :key="name"
                          :class="['tag','is-rounded','module-legend-pill',`module-${index + 1}`]">
                          {{ `${name} (${atus_module_name_to_abbrev[name]})` }}
                    </span>
                  </div>
                </fieldset>
              </div>
            </dropdown>

            <dropdown title="Show CPS Supplement Labels">
              <div class="dropdown-item">
                <ul class="checkbox-list">
                  <li class="parent">
                    <input type="checkbox" id="select_all_topics_checkbox" ref="select_all_topics_checkbox"
                           @change="all_topics_or_none">
                    <label for="select_all_topics_checkbox"><b>Show All</b></label>
                  </li>
                  <li v-for="topic in sorted_cps_supplement_topics" :key="topic.index">
                    <input type="checkbox" :id="'topic_cps_' + topic.index" :checked="topic['picked']"
                           @change="toggle_topic($event, topic);unselect_all_topics($event)">
                    <label :for="'topic_cps_' + topic.index">{{ topic.name }}</label>
                  </li>
                </ul>
              </div>
            </dropdown>

            <dropdown title="ATUS to CPS Linking Options">
              <div class="dropdown-item">
                <fieldset>
                  <legend></legend>
                  <div v-for="v in viewing_options">
                    <input type="radio" :id="v.id" name="options" :value="v.id" v-model="selected_viewing_option"/>
                    <label :for="v.id">{{ v.label }}</label>
                  </div>
                </fieldset>
              </div>
            </dropdown>

            <atus-csv-download-button />
          </div>
          <!-- Row 2: colorblind toggle -->
          <div class="filters-footer">
            <div class="colorblind-toggle">
              <span v-show="shouldShowTooltipsToggle">
                <label>
                  <input type="checkbox" v-model="tooltips_enabled"/>
                  Display Tooltips
                </label>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </header>
</template>

<script setup>
import AtusCsvDownloadButton from "./AtusCsvDownloadButton.vue";
import Dropdown from "./Dropdown.vue";
import {computed, inject, ref} from "vue";

const atus_modules = inject('atus_modules');
const atus_samples = inject('atus_samples');
const cps_supplement_topics = inject('cps_supplement_topics');
const tooltips_enabled = inject('tooltips_enabled');
const viewing_options = inject('viewing_options');
const selected_viewing_option = inject('selected_viewing_option');
const is_popup_open = inject('is_popup_open');
const shouldShowTooltipsToggle = inject('shouldShowTooltipsToggle');
const appDisplayName = inject('appDisplayName');
const select_all_topics_checkbox = ref(null);

const sorted_atus_module_names = computed(() =>
    Object.keys(atus_modules.value ?? {}).sort((a, b) => a.localeCompare(b))
);

const sorted_cps_supplement_topics = computed(() => {
  return Object.values(cps_supplement_topics.value).sort((a, b) => a.name.localeCompare(b.name));
});

const atus_module_name_to_pill_class = computed(() => {
  const map = {};
  sorted_atus_module_names.value.forEach((name, index) => {
    map[name] = `module-${index + 1}`;
  });
  return map;
});

const atus_module_name_to_abbrev = computed(() => {
  const map = {};
  sorted_atus_module_names.value.forEach((name) => {
    map[name] = make_atus_module_abbrev(name);
  });
  return map;
});

const toggle_year = (event, year) => {
  const selected_value = event.target.checked;
  atus_samples.value[year.name].picked = selected_value
  atus_samples.value[year.name].modules.forEach((module) => {
    let all_module_years_selected = true;
    atus_modules.value[module].years.forEach((year) => {
      if (!atus_samples.value[year].picked) {
        all_module_years_selected = false;
      }
    });
    atus_modules.value[module].picked = all_module_years_selected;
  });
}

const years_all_selected = () => {
  if (atus_samples.value) {
    return Object.keys(atus_samples.value).filter((y) => !atus_samples.value[y].picked).length === 0;
  } else {
    return false;
  }
}

const toggle_years_select_all = (event) => {
  Object.keys(atus_samples.value).forEach((year) => {
    atus_samples.value[year].picked = (event.target.checked === true);
  });
  Object.keys(atus_modules.value).forEach((module) => {
    atus_modules.value[module].picked = (event.target.checked === true);
  });
}

const toggle_topic = (event, topic) => {
  cps_supplement_topics.value[topic.name].picked = (event.target.checked === true);
}

const unselect_all_topics = (event) => {
  if (event.target.checked === true) {

    var total_topics = Object.keys(cps_supplement_topics.value).length;
    var total_selected_topics = Object.keys(cps_supplement_topics.value).filter((key) => cps_supplement_topics.value[key].picked).length;

    if (total_selected_topics === total_topics) {
      select_all_topics_checkbox.value.checked = true;
    }
  } else {
    select_all_topics_checkbox.value.checked = false;
  }
}

const all_topics_or_none = (event) => {
  var supplement_topics = Object.keys(cps_supplement_topics.value);

  if (event.target.checked) {
    for (let i = 0; i < supplement_topics.length; i++) {
      cps_supplement_topics.value[supplement_topics[i]].picked = true
    }
  } else {
    for (let i = 0; i < supplement_topics.length; i++) {
      cps_supplement_topics.value[supplement_topics[i]].picked = false
    }
  }
}

// module abbreviations are concatenations of 1st letter of each word ("and" is ignored) in module name
function make_atus_module_abbrev(moduleName) {
  return moduleName
      .split(/\s+/)             
      .filter(w => w.toLowerCase() !== 'and')
      .map(w => w[0].toUpperCase())           
      .join('');
}

function sorted_atus_modules_for_year(year) {
  return [...(atus_samples.value[year].modules ?? [])]
      .sort((a, b) => a.localeCompare(b));
}
 
</script>

<style lang="scss" scoped>
@use "../styles/variables" as vars;

#header {
  background-color: vars.$ipums_blue;
  padding: 5px 30px;

  #header_image {
    grid-column: 1;
    grid-row: 1 / span 2;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  #topics_legend {
    grid-column: 2;
    grid-row: 2;
    padding-left: 3px;

    label {
      padding-left: 3px;
    }
  }

  .header-title-row {
    grid-column: 2;
    grid-row: 1;
    display: grid;
    grid-template-columns: auto auto;
    justify-content: center;
    gap: 0.5rem;
    justify-self: start;
    padding-left: 7rem;

    .title {
      color: white;
      text-align: center;
    }
  }

  .colorblind-toggle {
    font-size: 0.75rem;
    color: white;
    min-height: 1.1rem;
  }

  .filters_container {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  #header_image img {
    width: 262px;
    height: 72px;
  }

  .filters-row {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .filters-footer {
    display: flex;
    align-items: center;
  }

  .header-grid {
    display: grid;
    grid-template-columns: 350px 1fr;
    grid-template-rows: auto auto;
    column-gap: 1rem;
    margin: 10px 0;
  }

  // begin module pills
  .module-legend-pill {
    height: 1.6em; 
    line-height: 1.2;
    padding-top: 0.1rem;
    padding-bottom: 0.1rem;
  }

  .module-1 {
    background-color: #ffa500;
  }

  .module-2 {
    background-color: #acf;
  }

  .module-3 {
    background-color: #6ec;
  }

  .module-4 {
    background-color: #ffdb58;
  }

  .module-legend-grid {
    display: grid;
    grid-template-columns: auto auto; /* 2 columns */
    gap: 0.4rem 0.4rem;
    justify-items: start;
    padding-bottom: 0.5rem;
  }
  
  .year-row {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .year-modules {
    display: inline-flex;
    gap: 0.25rem;
    flex-wrap: wrap;
  }

  .year-module-pill {
    height: 1.25em;
    line-height: 1.05;
    padding: 0 0.45em;
    font-size: .875em;
  }
  // end module pills
}

.checkbox-list {
  li {
    padding-left: 1rem;
  }

  li.parent {
    padding-left: inherit;
  }
}

</style>